<script setup lang="ts">
import { ref, onMounted, nextTick } from 'vue'
import axios from 'axios'
import { useRoute, useRouter } from 'vue-router'
import OnboardingPersonalInfosForm from '@/views/collectivites/onboarding/OnboardingPersonalInfosForm.vue'
import OnboardingIdentityDocumentsForm from '@/views/collectivites/onboarding/OnboardingIdentityDocumentsForm.vue'
import OnboardingPayslipDocumentsForm from '@/views/collectivites/onboarding/OnboardingPayslipDocumentsForm.vue'

const route = useRoute()
const router = useRouter()
const loading = ref(true)
const submitting = ref(false)
const errorMessage = ref('')
const successMessage = ref('')
const detail = ref<any>(null)
const collectivites = ref<any[]>([])
const typesPieceIdentites = ref<any>({})
const personalFormRef = ref<any>(null)

const fullUser = ref<any>(null)

async function fetchDetail() {
  loading.value = true
  errorMessage.value = ''
  try {
    const [kycRes, formRes, userRes] = await Promise.all([
      axios.get(`/backoffice/kyc/members/${route.params.id}`),
      axios.get('/backoffice/onboarding/get_form_datas'),
      axios.get(`/backoffice/users/get/${route.params.id}`),
    ])
    detail.value = kycRes.data
    fullUser.value = userRes.data
    collectivites.value = formRes.data?.collectivites || []
    typesPieceIdentites.value = formRes.data?.types_piece_identites || {}
    loading.value = false
    await nextTick()
    prefillPersonalForm()
  } catch (e: any) {
    errorMessage.value = e.response?.data?.message || 'Erreur lors du chargement'
    loading.value = false
  }
}

function prefillPersonalForm() {
  if (!personalFormRef.value) return
  const u = fullUser.value || detail.value?.user
  if (!u) return
  const k = detail.value?.kyc
  Object.assign(personalFormRef.value.formData, {
    nom: k?.nom ?? u.nom ?? null,
    prenom: k?.prenom ?? u.prenom ?? null,
    date_naissance: (k?.date_naissance ?? u.date_naissance) ? String(k?.date_naissance ?? u.date_naissance).substring(0, 10) : null,
    age: u.age ?? 0,
    date_engagement: u.date_engagement ? String(u.date_engagement).substring(0, 10) : null,
    anciennete: u.anciennete ?? 0,
    collectivite_id: u.collectivite_id ?? null,
    salaire: u.salaire ?? '',
    quotite_cessible_pourcent: u.quotite_cessible_pourcent ?? '',
    quotite_cessible_montant: u.quotite_cessible_montant ?? '',
    telephone: u.telephone ?? null,
    email: u.email ?? null,
  })
}

async function onPersonalFormSaved(payload: any) {
  submitting.value = true
  errorMessage.value = ''
  successMessage.value = ''
  try {
    await axios.put(`/backoffice/kyc/members/${route.params.id}`, {
      nom: payload.nom,
      prenom: payload.prenom,
      date_naissance: payload.date_naissance,
    })
    successMessage.value = 'Profil KYC enregistré'
    await fetchDetail()
  } catch (e: any) {
    errorMessage.value = e.response?.data?.message || 'Erreur lors de la sauvegarde'
  } finally {
    submitting.value = false
  }
}

function triggerSavePersonal() {
  personalFormRef.value?.onSubmit()
}

async function uploadKycDocument(typeDocument: string, file: any, extra: { numero?: string; expiration?: string } = {}) {
  const fd = new FormData()
  fd.append('type_document', typeDocument)
  fd.append('fichier', file)
  if (extra.numero) fd.append('numero', extra.numero)
  if (extra.expiration) fd.append('expiration', extra.expiration)
  await axios.post(`/backoffice/kyc/members/${route.params.id}/documents`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

async function onIdentityDocumentsSaved(payload: any) {
  submitting.value = true
  errorMessage.value = ''
  successMessage.value = ''
  try {
    const typeLabel = typesPieceIdentites.value[payload.type_piece_identite] || 'cni'
    const isPassport = String(typeLabel).toLowerCase().includes('pass')

    if (payload.photo?.file) {
      await uploadKycDocument('photo', payload.photo.file)
    }
    if (payload.piece_recto?.file) {
      await uploadKycDocument(isPassport ? 'passeport' : 'cni_recto', payload.piece_recto.file, {
        numero: payload.numero_piece_identite,
      })
    }
    if (payload.piece_verso?.file && !isPassport) {
      await uploadKycDocument('cni_verso', payload.piece_verso.file, {
        numero: payload.numero_piece_identite,
      })
    }
    successMessage.value = 'Pièces d\'identité enregistrées'
    await fetchDetail()
  } catch (e: any) {
    errorMessage.value = e.response?.data?.message || 'Erreur lors de l\'enregistrement des pièces'
  } finally {
    submitting.value = false
  }
}

async function onPayslipDocumentsSaved(payload: any) {
  submitting.value = true
  errorMessage.value = ''
  successMessage.value = ''
  try {
    if (payload.bulletin1?.file) await uploadKycDocument('bulletin1', payload.bulletin1.file)
    if (payload.bulletin2?.file) await uploadKycDocument('bulletin2', payload.bulletin2.file)
    if (payload.bulletin3?.file) await uploadKycDocument('bulletin3', payload.bulletin3.file)
    successMessage.value = 'Bulletins de salaire enregistrés'
    await fetchDetail()
  } catch (e: any) {
    errorMessage.value = e.response?.data?.message || 'Erreur lors de l\'enregistrement des bulletins'
  } finally {
    submitting.value = false
  }
}

async function validateKyc() {
  if (!confirm('Confirmer la validation du KYC de ce membre ?')) return
  submitting.value = true
  errorMessage.value = ''
  try {
    await axios.post(`/backoffice/kyc/members/${route.params.id}/validate`)
    successMessage.value = 'KYC validé'
    await fetchDetail()
  } catch (e: any) {
    errorMessage.value = e.response?.data?.message || 'Erreur lors de la validation'
  } finally {
    submitting.value = false
  }
}

async function rejectKyc() {
  if (!confirm('Confirmer le rejet du KYC de ce membre ?')) return
  submitting.value = true
  errorMessage.value = ''
  try {
    await axios.post(`/backoffice/kyc/members/${route.params.id}/reject`)
    successMessage.value = 'KYC rejeté'
    await fetchDetail()
  } catch (e: any) {
    errorMessage.value = e.response?.data?.message || 'Erreur lors du rejet'
  } finally {
    submitting.value = false
  }
}

async function deleteDocument(docId: number) {
  if (!confirm('Supprimer ce document ?')) return
  submitting.value = true
  try {
    await axios.delete(`/backoffice/kyc/members/${route.params.id}/documents/${docId}`)
    successMessage.value = 'Document supprimé'
    await fetchDetail()
  } catch (e: any) {
    errorMessage.value = e.response?.data?.message || 'Erreur lors de la suppression'
  } finally {
    submitting.value = false
  }
}

function statutLabel(): { label: string; cls: string } {
  const s = detail.value?.kyc?.statut
  if (!detail.value?.kyc) return { label: 'Pas de KYC', cls: 'tag-state warning stroke' }
  if (s === 'verified') return { label: 'Validé', cls: 'tag-state success stroke' }
  if (s === 'rejected') return { label: 'Rejeté', cls: 'tag-state danger stroke' }
  return { label: 'En attente', cls: 'tag-state warning stroke' }
}

onMounted(fetchDetail)
</script>

<template>
  <section class="container-full-1 bg-grey-50">
    <div class="page-space-40"></div>
    <button class="btn white2 mb-4" @click="router.back()">← Retour</button>

    <div v-if="errorMessage" class="alert alert-danger mb-4">{{ errorMessage }}</div>
    <div v-if="successMessage" class="alert alert-success mb-4">{{ successMessage }}</div>

    <div v-if="loading" class="flex-center py-10">
      <div class="loader dark"></div>
    </div>

    <div v-else-if="detail">
      <!-- En-tête membre + statut + actions -->
      <div class="s20 widget p-6 mb-5">
        <div style="display: flex; align-items: center; gap: 1.5rem;">
          <div style="width: 96px; height: 96px; min-width: 96px; flex: 0 0 96px; border-radius: 15px; overflow: hidden;">
            <img :src="detail.user.photo_url || '/assets/users_avatar/600w/user.jpg'" style="width: 100%; height: 100%; object-fit: cover; display: block;" />
          </div>
          <div style="flex: 1 1 auto; min-width: 0;">
            <h3>{{ detail.user.nom }} {{ detail.user.prenom }}</h3>
            <p>{{ detail.user.email }}</p>
            <p>{{ detail.user.telephone }}</p>
            <p class="mt-2">
              Statut KYC :
              <span :class="statutLabel().cls">{{ statutLabel().label }}</span>
              <span v-if="detail.kyc?.validator" class="ml-2 text-sm text-grey-500">
                (par {{ detail.kyc.validator.nom_complet }})
              </span>
            </p>
          </div>
          <div class="flex gap-3">
            <button class="btn-ultra" :disabled="submitting || !detail.kyc" @click="validateKyc">
              <div class="btn-show">
                <img src="/icons/icon-valider.svg" class="btn-icon icon-neutral" alt="" />
                <span>Valider</span>
              </div>
            </button>
            <button class="btn-ultra danger" :disabled="submitting || !detail.kyc" @click="rejectKyc">
              <div class="btn-show">
                <img src="/icons/icon-denied.svg" class="btn-icon icon-white" alt="" />
                <span style="color: var(--color-white);">Rejeter</span>
              </div>
            </button>
          </div>
        </div>
      </div>

      <!-- Profil KYC : OnboardingPersonalInfosForm -->
      <div class="s20 widget p-6 mb-5">
        <h4 class="mb-4">Profil KYC</h4>
        <OnboardingPersonalInfosForm
          ref="personalFormRef"
          :collectivites="collectivites"
          :hide-collectivite-field="false"
          :hide-submit-button="true"
          :full-width="true"
          @saved="onPersonalFormSaved"
        />
        <div class="flex-right mt-3">
          <button class="btn-ultra" :disabled="submitting" @click="triggerSavePersonal">
            <div class="btn-show">
              <img src="/icons/icon-valider.svg" class="btn-icon icon-neutral" alt="" />
              <span>Enregistrer</span>
            </div>
          </button>
        </div>
      </div>

      <div style="clear: both;"></div>

      <!-- Pièce d'identité + Bulletins : côte à côte sur desktop -->
      <div class="mb-5" style="display: flex; gap: 1.5rem; flex-wrap: wrap; align-items: flex-start; clear: both;">
        <div class="widget p-6" style="flex: 1 1 calc(50% - 0.75rem); min-width: 320px;">
          <h4 class="mb-4">Pièce d'identité</h4>
          <OnboardingIdentityDocumentsForm
            v-if="Object.keys(typesPieceIdentites).length > 0"
            :user="detail.user"
            :types_piece_identites="typesPieceIdentites"
            :hide-header="true"
            @saved="onIdentityDocumentsSaved"
          />
          <div v-else class="text-grey-500 py-3">Chargement des types de pièces...</div>
        </div>

        <div class="widget p-6" style="flex: 1 1 calc(50% - 0.75rem); min-width: 320px;">
          <h4 class="mb-4">Bulletins de salaire</h4>
          <OnboardingPayslipDocumentsForm
            :form-params="{ user: detail.user, photo: null }"
            :loading="submitting"
            :hide-header="true"
            :hide-title="true"
            :hide-previous-link="true"
            @saved="onPayslipDocumentsSaved"
            @previous="() => {}"
          />
        </div>
      </div>

      <!-- Documents existants -->
      <div class="s20 widget p-6 mb-5">
        <h4 class="mb-4">Documents existants</h4>
        <div v-if="!detail.kyc?.documents?.length" class="text-grey-500 py-3">
          Aucun document fourni.
        </div>
        <div v-else>
          <div v-for="d in detail.kyc.documents" :key="d.id" class="flex align-items-center gap-4 py-3 border-bottom">
            <div style="flex: 1 1 auto;">
              <strong>{{ d.typeDocument }}</strong>
              <span v-if="d.numero" class="ml-2 text-sm text-grey-500">N° {{ d.numero }}</span>
              <span v-if="d.expiration && d.expiration !== '--'" class="ml-2 text-sm text-grey-500">
                Exp. {{ d.expiration.substring(0, 10) }}
              </span>
            </div>
            <a v-if="d.fichierUrl" :href="d.fichierUrl" target="_blank" class="btn white2">Voir</a>
            <button class="btn-ultra danger" :disabled="submitting" @click="deleteDocument(d.id)">
              <div class="btn-show">
                <span style="color: var(--color-white);">Supprimer</span>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

